import styled, { css } from 'styled-components';
import { buttonBase } from '@/styles/mixins';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'gold';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
}

const variantStyles = {
  primary: css`
    background: ${({ theme }) => theme.colors.primaryGradient};
    color: ${({ theme }) => theme.colors.textInverse};
    box-shadow: ${({ theme }) => theme.shadows.colored};
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 28px -6px rgba(255, 107, 53, 0.4);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `,
  secondary: css`
    background: ${({ theme }) => theme.colors.secondaryGradient};
    color: ${({ theme }) => theme.colors.textInverse};
    box-shadow: ${({ theme }) => theme.shadows.glowGold};
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 28px -6px rgba(255, 179, 71, 0.4);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `,
  gold: css`
    background: linear-gradient(135deg, #FFB347 0%, #FFD700 50%, #FFB347 100%);
    color: #1A1A1A;
    font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 28px -6px rgba(255, 215, 0, 0.4);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `,
  outline: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.primary};
    border: 2px solid ${({ theme }) => theme.colors.primary};
    
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.primaryLight};
      border-color: ${({ theme }) => theme.colors.primaryHover};
    }
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.text};
    
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.backgroundTertiary};
    }
  `,
  danger: css`
    background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
    color: ${({ theme }) => theme.colors.textInverse};
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px -4px rgba(239, 68, 68, 0.4);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  `,
};

const sizeStyles = {
  sm: css`
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.typography.fontSizes.xs};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    min-height: 32px;
  `,
  md: css`
    padding: 10px ${({ theme }) => theme.spacing.lg};
    font-size: ${({ theme }) => theme.typography.fontSizes.sm};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    min-height: 44px;
    
    @media (max-width: 480px) {
      padding: 10px ${({ theme }) => theme.spacing.md};
      font-size: ${({ theme }) => theme.typography.fontSizes.xs};
    }
  `,
  lg: css`
    padding: 14px ${({ theme }) => theme.spacing.xl};
    font-size: ${({ theme }) => theme.typography.fontSizes.md};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    min-height: 52px;
    
    @media (max-width: 480px) {
      padding: 12px ${({ theme }) => theme.spacing.lg};
      font-size: ${({ theme }) => theme.typography.fontSizes.sm};
      min-height: 48px;
    }
  `,
};

const StyledButton = styled.button<ButtonProps>`
  ${buttonBase}
  ${({ variant = 'primary' }) => variantStyles[variant]}
  ${({ size = 'md' }) => sizeStyles[size]}
  ${({ fullWidth }) => fullWidth && css`width: 100%;`}
  
  position: relative;
  overflow: hidden;
  
  /* Shine effect on hover */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  ${({ isLoading }) => isLoading && css`
    color: transparent;
    pointer-events: none;
    
    &::after {
      content: '';
      position: absolute;
      width: 18px;
      height: 18px;
      top: 50%;
      left: 50%;
      margin-left: -9px;
      margin-top: -9px;
      border: 2px solid white;
      border-radius: 50%;
      border-right-color: transparent;
      animation: spin 0.6s linear infinite;
    }
    
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `}
`;

export const Button: React.FC<ButtonProps> = ({ children, ...props }) => {
  return <StyledButton {...props}>{children}</StyledButton>;
};
